import React, { useState, useEffect } from 'react';
import { HiMenu, HiX, HiPhone, HiArrowRight } from 'react-icons/hi';

const navLinks = [
  { name: 'Mumbai 3.0', href: '#overview' },
  { name: 'Why Invest', href: '#highlights' },
  { name: 'Infrastructure', href: '#infrastructure' },
  { name: 'Services', href: '#services' },
  { name: 'About', href: '#about' },
  { name: 'Gallery', href: '#gallery' },
  { name: 'Contact', href: '#contact' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
      const sections = navLinks.map((l) => l.href.replace('#', ''));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSection(sections[i]);
          return;
        }
      }
      setActiveSection('home');
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.04),0_8px_30px_rgba(0,0,0,0.06)]'
          : 'bg-transparent'
      }`}
    >
      {/* Top info strip — only on hero */}
      {!isScrolled && (
        <div className="hidden lg:flex items-center justify-between max-w-7xl mx-auto px-8 pt-4 pb-2">
          <p className="text-white/50 text-[10px] tracking-[0.2em] uppercase font-medium">
            Premium Plot Investment — MahaMumbai Region
          </p>
          <a href="tel:+918550015003" className="text-accent-300 text-[11px] tracking-wider font-medium flex items-center gap-2 hover:text-white transition-colors">
            <HiPhone className="text-xs" />
            85 5001 5003
          </a>
        </div>
      )}

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isScrolled ? 'py-2' : 'py-2 lg:py-0'}`}>
        <div className="flex items-center justify-between h-14 lg:h-16">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3 group relative z-10">
            <img 
              src="/logo.png" 
              alt="Prime Acres Logo" 
              className="w-16 h-14 object-contain"
            />
            <div className="leading-tight hidden sm:block">
              <span className={`text-base font-heading tracking-wide transition-colors duration-500 block ${isScrolled ? 'text-dark-800' : 'text-white'}`}>
                Prime Acres
              </span>
              <span className={`text-[8px] tracking-[0.3em] uppercase font-semibold transition-colors duration-500 ${isScrolled ? 'text-accent-500' : 'text-accent-300/80'}`}>
                Plot Your Future
              </span>
            </div>
          </a>

          {/* Desktop Nav — centered pill */}
          <div className={`hidden lg:flex items-center rounded-full px-1.5 py-1 transition-all duration-500 ${
            isScrolled ? 'bg-gray-50 border border-gray-100' : 'bg-white/10 backdrop-blur-md border border-white/10'
          }`}>
            {navLinks.map((link) => {
              const isActive = activeSection === link.href.replace('#', '');
              return (
                <a
                  key={link.name}
                  href={link.href}
                  className={`relative px-2.5 xl:px-3.5 py-1.5 text-[10px] xl:text-[11px] font-medium tracking-[0.06em] uppercase transition-all duration-300 rounded-full whitespace-nowrap ${
                    isActive
                      ? isScrolled
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-white/20 text-white'
                      : isScrolled
                        ? 'text-dark-400 hover:text-dark-700 hover:bg-gray-100'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.name}
                </a>
              );
            })}
          </div>

          {/* Right side CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {isScrolled && (
              <a href="tel:+918550015003" className="text-dark-500 text-[11px] font-medium tracking-wider hover:text-primary-600 transition-colors flex items-center gap-1.5">
                <HiPhone className="text-xs" />
                85 5001 5003
              </a>
            )}
            <button onClick={() => window.dispatchEvent(new Event('openContactModal'))} className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[11px] font-semibold tracking-wider uppercase transition-all duration-500 ${
              isScrolled
                ? 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg'
                : 'bg-white text-dark-800 hover:bg-accent-50 hover:shadow-lg'
            }`}>
              Enquire
              <HiArrowRight className="text-xs" />
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              isScrolled ? 'bg-gray-100 text-dark-700' : 'bg-white/10 text-white backdrop-blur-sm border border-white/10'
            }`}
          >
            {isMobileMenuOpen ? <HiX className="text-xl" /> : <HiMenu className="text-xl" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu — rounded sheet */}
      <div className={`lg:hidden transition-all duration-500 overflow-hidden ${isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-white/95 backdrop-blur-xl mx-3 mb-3 rounded-2xl shadow-premium-lg px-5 py-6 border border-gray-100/50">
          <div className="grid grid-cols-2 gap-2 mb-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-2.5 px-4 text-dark-500 hover:bg-primary-50 hover:text-primary-700 font-medium text-[12px] tracking-wider uppercase rounded-xl transition-all duration-300 text-center"
              >
                {link.name}
              </a>
            ))}
          </div>
          <div className="border-t border-gray-100 pt-4 flex flex-col gap-2.5">
            <a href="tel:+918550015003" className="flex items-center justify-center gap-2 py-2.5 text-primary-600 font-semibold text-sm">
              <HiPhone /> 85 5001 5003
            </a>
            <button onClick={() => { setIsMobileMenuOpen(false); window.dispatchEvent(new Event('openContactModal')); }} className="flex items-center justify-center gap-2 bg-primary-600 text-white rounded-xl py-3 text-[12px] font-semibold tracking-wider uppercase">
              Enquire Now <HiArrowRight />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
